import 'tippy.js/dist/tippy.css';
import React, { useEffect } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import { useGate } from 'statsig-react';
import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faMagnifyingGlass } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { styled, theme } from '@components-shared/src/stitches';
import { getSearchPageResults } from '@components/Explore/helpers';
import { SearchResults } from '@components/SearchBar/SearchBarResults';
import { useGlobalSearchBarInput } from '@components/SearchBar/useGlobalSearchBarInput';
import {
  ClearSearchButton,
  SearchBarContainer,
  SearchInput,
  SearchInputContainer,
} from '@components/main-components/search/SearchInput';
import { useMediaQuery } from '@context/mediaQuery';
import { useListenElementOffsetWidth } from '@hooks/useListenElementWidth';
import { useStableCallback } from '@hooks/useStableCallback';
import { useHeaderStore } from '@store/header';
import {
  BLUR_BACKGROUND_CORE_ACTION_V1_PATHS,
  BLUR_BACKGROUND_PATHS,
  FEATURE_GATES,
} from '@utils/constants';
import { LazyTippy } from '../LazyTippy';

export function DesktopSearchBar() {
  const { close, isSearchOpen } = useHeaderStore();
  const { pathname } = useRouter();
  const { tablet } = useMediaQuery();

  const { focus, focusOn, focusOff, barRawInputText, handleInputChange, clearText, hasHadFocus } =
    useGlobalSearchBarInput();

  const showSearchResults = hasHadFocus || isSearchOpen;

  const containerRef = React.useRef<HTMLDivElement>(null);

  const width = useListenElementOffsetWidth(containerRef);

  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const [enableTippyAnimation, setEnableTippyAnimation] = React.useState(true);

  // when search bar is focused, enable tippy animation
  useEffect(() => {
    if (!searchInputRef.current) return;

    const searchRef = searchInputRef.current;

    const focus = () => {
      setEnableTippyAnimation(true);
    };

    searchRef.addEventListener('focus', focus);

    return () => {
      searchRef.removeEventListener('focus', focus);
    };
  }, []);

  const ClearSearch = !barRawInputText ? null : (
    <ClearSearchButton onClick={clearText} aria-label="Clear search input">
      <FontAwesomeIcon icon={faXmark} size="lg" />
    </ClearSearchButton>
  );

  const onDesktopRowClick = useStableCallback(() => {
    close();
    focusOff();
    setEnableTippyAnimation(false);
    clearText();
  });

  const onSearchClick = useStableCallback(() => {
    close();
    focusOff();
    setEnableTippyAnimation(false);
  });

  const { value: coreActionV1 } = useGate(FEATURE_GATES.CORE_ACTIONS_V1);

  const isBlurBackground = React.useMemo(() => {
    if (!coreActionV1) {
      return BLUR_BACKGROUND_CORE_ACTION_V1_PATHS.includes(pathname);
    }
    return BLUR_BACKGROUND_PATHS.includes(pathname);
  }, [coreActionV1, pathname]);

  /**
   * Keyboard shortcuts
   * - Cmd/Ctrl + K to focus on search input
   * - Esc to close search input
   */
  const handleKey = useStableCallback((event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
      searchInputRef.current?.focus();
      focusOn();
      setEnableTippyAnimation(true);
    }

    if (event.key === 'Escape') {
      searchInputRef.current?.blur();
      close();
      focusOff();
      setEnableTippyAnimation(false);
      clearText();
    }
  });

  // Keyboard shortcuts
  useEffect(() => {
    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [handleKey]);

  const searchElements = (
    <SearchBarContainer ref={containerRef}>
      <SearchInputContainer main isBlurBackground={isBlurBackground} isActive={focus}>
        {tablet && (
          <>
            <div className="search-icon">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </div>
          </>
        )}
        <SearchInput
          ref={searchInputRef}
          type="text"
          value={barRawInputText}
          placeholder="Search for anything on Sound"
          onChange={handleInputChange}
          onSelect={focusOn}
          onClick={focusOn}
          isBlurBackground={isBlurBackground}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onSearchClick();
              getSearchPageResults({ inputText: barRawInputText, useRouter: true });
            }
          }}
        />
        {ClearSearch}
      </SearchInputContainer>
    </SearchBarContainer>
  );

  return (
    <LazyTippy
      arrow={false}
      placement="bottom"
      visible={focus}
      onClickOutside={focusOff}
      interactive
      maxWidth="none"
      animation={enableTippyAnimation ? 'fade' : false}
      zIndex={parseInt(theme.zIndices.search.value, 10)}
      content={
        showSearchResults && (
          <div
            style={{
              minWidth: width,
            }}
          >
            <SearchResultsWrapper>
              <SearchResults
                showSearchResults={showSearchResults}
                onRowClick={onDesktopRowClick}
                onSearchClick={onSearchClick}
              />
            </SearchResultsWrapper>
          </div>
        )
      }
    >
      {searchElements}
    </LazyTippy>
  );
}

const SearchResultsWrapper = styled('div', {
  overflow: 'hidden',
  background: '$white',
  boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.18)',
  borderRadius: 8,
});
